/* eslint-disable react-hooks/refs */
import './styles/Tooltip.scss';

import type { IconWeight } from '@phosphor-icons/react';
import { Info } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'motion/react';
import React, {
  cloneElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useClassHelper } from '../../hooks';
import { Paragraph } from '../Typography';

const componentName = 'c-Tooltip';

export type ToolTipAlign = 'top' | 'bottom' | 'left' | 'right';

interface BaseProps {
  align: ToolTipAlign;
  contentClassName?: string;
  wrapperClassName?: string;
  delay?: number;
}

interface SimpleTooltipProps extends BaseProps {
  label: string;
  size: number;
  weight: IconWeight;
  color: string;
}

interface NodeTooltipProps extends BaseProps {
  trigger: ReactNode;
  content: ReactNode;
}

// Union type final
type TooltipProps = SimpleTooltipProps | NodeTooltipProps;

export const Tooltip = (props: TooltipProps) => {
  const {
    align,
    contentClassName = '',
    wrapperClassName = '',
    delay = 0,
  } = props;
  const triggerRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [adjustedAlign, setAdjustedAlign] = useState(align);
  const c = useClassHelper(componentName);

  // Check if props match SimpleTooltipProps
  const isSimpleTooltip =
    'label' in props &&
    'size' in props &&
    'weight' in props &&
    'color' in props;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const adjustPosition = () => {
      if (!triggerRef.current || !contentRef.current) {
        return;
      }

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newAlign = align;

      if (align === 'right') {
        if (triggerRect.right + contentRect.width > viewportWidth) {
          newAlign = 'left';
        }
      } else if (align === 'left') {
        if (triggerRect.left - contentRect.width < 0) {
          newAlign = 'right';
        }
      } else if (align === 'bottom') {
        if (triggerRect.bottom + contentRect.height > viewportHeight) {
          newAlign = 'top';
        }
      } else if (align === 'top') {
        if (triggerRect.top - contentRect.height < 0) {
          newAlign = 'bottom';
        }
      }

      setAdjustedAlign(newAlign);
    };

    adjustPosition();

    window.addEventListener('resize', adjustPosition);
    return () => window.removeEventListener('resize', adjustPosition);
  }, [isOpen, align]);

  const positionStyles: Record<string, React.CSSProperties> = {
    top: {
      bottom: '100%',
      left: '50%',
      marginBottom: '0.5rem',
    },
    bottom: {
      top: '100%',
      left: '50%',
      marginTop: '0.5rem',
    },
    left: {
      right: '100%',
      top: '50%',
      marginRight: '0.5rem',
    },
    right: {
      left: '100%',
      top: '50%',
      marginLeft: '0.5rem',
    },
  };

  const getTransform = (align: string) => {
    switch (align) {
      case 'top':
      case 'bottom':
        return { x: '-50%' };
      case 'left':
      case 'right':
        return { y: '-50%' };
      default:
        return { x: '-50%' };
    }
  };

  return (
    <div id="tooltip" className={`${c('base')} ${wrapperClassName}`}>
      {'trigger' in props &&
        props.trigger &&
        cloneElement(props.trigger as any, {
          ref: triggerRef,
          onMouseEnter: () => setIsOpen(true),
          onMouseLeave: () => setIsOpen(false),
          onFocus: () => setIsOpen(true),
          onBlur: () => setIsOpen(false),
          'aria-describedby': isOpen ? 'tooltip-content' : undefined,
          // A trigger that already names itself (e.g. an icon button with its
          // own aria-label) keeps that name; the generic label is a fallback.
          'aria-label':
            (props.trigger as any).props?.['aria-label'] ?? 'more information',
        })}

      {/* Render default trigger for SimpleTooltipProps */}
      {isSimpleTooltip && (
        <span
          ref={triggerRef}
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          aria-describedby={isOpen ? 'tooltip-content' : undefined}
          tabIndex={0}
          className={c('__tooltip-trigger')}
        >
          <Info
            role="img"
            size={props.size}
            color={props.color}
            weight={props.weight}
            aria-hidden={false}
            aria-label="Additional information"
          />
        </span>
      )}

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={contentRef}
            role="tooltip"
            id="tooltip-content"
            className={c('__tooltip')}
            style={positionStyles[adjustedAlign]}
            initial={{ opacity: 0, scale: 0.9, ...getTransform(adjustedAlign) }}
            animate={{ opacity: 1, scale: 1, ...getTransform(adjustedAlign) }}
            exit={{ opacity: 0, scale: 0.9, ...getTransform(adjustedAlign) }}
            transition={{ duration: 0.2, delay }}
            aria-live="polite"
          >
            <div className={`${c('__content')} ${contentClassName}`}>
              <Paragraph tag="span">
                {/* Render content for NodeTooltipProps */}
                {'content' in props && props.content}
                {/* Render label for SimpleTooltipProps */}
                {isSimpleTooltip && props.label}
              </Paragraph>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
